/**
 * Exemptify VAT Widget Handler
 *
 * This script ensures the Exemptify VAT widget is always visible and properly initialized
 * in all cart views (cart page and cart drawer).
 */

(function () {
  const config = {
    widgetId: "ExemptifyCartValidationSnippet",
    widgetClass: "exemptify-widget-container",
    maxRetries: 10,
    retryInterval: 300,
    debug: true,
  };

  const log = (message, type = "log") => {
    if (config.debug) {
      console[type](`[Exemptify]: ${message}`);
    }
  };

  let scriptLoaded = false;
  let initializationCount = 0;
  let retryCount = 0;
  let pendingInitializations = [];
  let initializationInterval = null;

  function checkExemptifyElements() {
    const container = document.getElementById(config.widgetId);
    return container !== null;
  }

  function makeWidgetVisible() {
    const containers = document.querySelectorAll(
      `#${config.widgetId}, .${config.widgetClass}`
    );
    if (containers.length === 0) return false;

    containers.forEach((container) => {
      container.style.display = "block";
      container.style.visibility = "visible";
      container.style.opacity = "1";

      container.setAttribute(
        "style",
        container.getAttribute("style") +
          " display: block !important; visibility: visible !important; opacity: 1 !important;"
      );
    });

    return true;
  }

  function initializeVatWidget(source = "manual") {
    initializationCount++;

    if (!makeWidgetVisible()) {
      log(
        `Widget elements not found in DOM (source: ${source}), will retry later`,
        "warn"
      );

      if (!pendingInitializations.includes(source)) {
        pendingInitializations.push(source);
      }

      return false;
    }

    if (!scriptLoaded) {
      log(`Script not loaded yet, queueing initialization (source: ${source})`);

      if (!pendingInitializations.includes(source)) {
        pendingInitializations.push(source);
      }

      return false;
    }

    if (typeof window.validateVatId === "function") {
      try {
        log(
          `Initializing VAT widget (source: ${source}, attempt #${initializationCount})`
        );
        window.validateVatId();
        makeWidgetVisible();
        retryCount = 0;
        return true;
      } catch (error) {
        log(`Error initializing VAT widget: ${error}`, "error");
        return false;
      }
    } else {
      log(
        `validateVatId function not found (source: ${source}), will retry`,
        "warn"
      );
      startPollingForFunction();
      return false;
    }
  }

  function startPollingForFunction() {
    if (initializationInterval) {
      clearInterval(initializationInterval);
    }

    retryCount = 0;

    initializationInterval = setInterval(() => {
      retryCount++;

      if (typeof window.validateVatId === "function") {
        clearInterval(initializationInterval);
        log(`Found validateVatId function after ${retryCount} retries`);

        processAllPendingInitializations();
      } else if (retryCount >= config.maxRetries) {
        clearInterval(initializationInterval);
        log(
          `Failed to find validateVatId function after ${retryCount} retries`,
          "error"
        );
      }
    }, config.retryInterval);
  }

  function processAllPendingInitializations() {
    if (pendingInitializations.length > 0) {
      log(
        `Processing ${pendingInitializations.length} pending initializations`
      );
      const source = pendingInitializations.join("+");
      initializeVatWidget(`pending:${source}`);
      pendingInitializations = [];
    }
  }

  function observeDOMChanges() {
    log("Setting up MutationObserver to watch for cart changes");

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (
                node.classList &&
                (node.classList.contains("cart__wrapper") ||
                  node.classList.contains("cart__contents") ||
                  node.id === "drawer-cart" ||
                  node.id === "main-cart")
              ) {
                log("Detected cart element added to DOM");
                setTimeout(() => {
                  initializeVatWidget("dom-mutation");
                }, 300);
              }

              if (
                node.id === config.widgetId ||
                node.classList.contains(config.widgetClass)
              ) {
                log("Detected Exemptify widget added to DOM");
                setTimeout(() => {
                  initializeVatWidget("widget-added");
                }, 300);
              }
            }
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function init() {
    log("Initializing Exemptify widget handler");

    document.addEventListener("exemptify:loaded", function () {
      log("Exemptify script loaded");
      scriptLoaded = true;
      processAllPendingInitializations();
    });

    document.addEventListener("cart-updated", function (event) {
      log("Cart updated event detected");
      setTimeout(() => {
        initializeVatWidget("cart-updated");
      }, 300);
    });

    document.addEventListener("DOMContentLoaded", function () {
      log("DOM loaded");
      initializeVatWidget("dom-loaded");
      observeDOMChanges();
    });

    document.addEventListener("modal:opened", function (event) {
      if (event.detail && event.detail.modalId === "cart-drawer") {
        log("Cart drawer opened");
        setTimeout(() => {
          initializeVatWidget("drawer-opened");
        }, 300);
      }
    });

    if (
      document.readyState === "complete" ||
      document.readyState === "interactive"
    ) {
      log("Page already loaded, initializing");
      initializeVatWidget("init");
      observeDOMChanges();
    }
  }

  init();
})();
